import { useCallback, useEffect, useMemo, useState } from "react";

import { HttpError, IHttpRequestConfig, isCanceledHttpRequest } from "@/browser";
import { hasCode, IErrorEntry } from "@/core/api";
import { backoff } from "@/core/backoff/service";
import { getBlob, getFilePreview } from "@/data/attachments";
import { AttachmentPreview } from "@/data/attachments/types/AttachmentPreview";
import { useCancelTokenSource } from "@/hooks/http";

export type FilePreviewError = "FILE_PREVIEW_UNAVAILABLE";

interface Payload {
  attachment: AttachmentPreview;
}

interface FilePreviewReturnState {
  activeAttachment?: AttachmentPreview;
  attachment: AttachmentPreview;
}

export const useFilePreview = ({ attachment }: Payload): FilePreviewReturnState => {
  const { refreshToken, source } = useCancelTokenSource();
  const [enrichedAttachment, setEnrichedAttachment] = useState<AttachmentPreview>(attachment);
  const [activeAttachmentSystemId, setActiveAttachmentSystemId] = useState<string>();

  const enrichAttachment = useCallback((attachment: AttachmentPreview) => {
    setEnrichedAttachment(() => {
      return attachment;
    });
  }, []);

  const handleFetch = useCallback(async (fileSystemId: string, httpConfig: IHttpRequestConfig) => {
    const meta = await getFilePreview({ fileSystemId }, httpConfig);

    if (!meta.url) {
      return Promise.reject();
    }

    const blob = await getBlob(meta.url);

    return URL.createObjectURL(blob);
  }, []);

  const previewBackOff = useCallback(
    (fileSystemId: string) => {
      refreshToken();

      return backoff(() => handleFetch(fileSystemId, { cancelToken: source.current.token }), {
        retries: 10,
        factor: 1,
        delay: 2000,
        shouldRetry,
      });
    },
    [handleFetch, refreshToken, source],
  );

  const setActiveAttachment = useCallback(
    async (attachment: AttachmentPreview) => {
      setActiveAttachmentSystemId(attachment.file.systemId);
      if (attachment.pdfUrl) {
        return;
      }
      try {
        enrichAttachment({ ...attachment, working: true, error: false });
        const pdfUrl = await previewBackOff(attachment.file.systemId);
        enrichAttachment({ ...attachment, pdfUrl, working: false, error: false });
      } catch (error) {
        if (isCanceledHttpRequest(error)) {
          enrichAttachment({ ...attachment, error: false, working: false });
          return;
        }
        enrichAttachment({ ...attachment, error: true, working: false });
      }
    },
    [enrichAttachment, previewBackOff],
  );

  useEffect(() => {
    setActiveAttachment(attachment);
  }, []);

  const activeAttachment = useMemo(() => {
    if (!activeAttachmentSystemId) {
      return;
    }
    return enrichedAttachment;
  }, [activeAttachmentSystemId, enrichedAttachment]);

  return {
    attachment: enrichedAttachment,
    activeAttachment,
  };
};

const shouldRetry = (err: HttpError<IErrorEntry<FilePreviewError>>) => {
  if (!err.payload) {
    return false;
  }
  return hasCode(err.payload, "FILE_PREVIEW_UNAVAILABLE");
};
